import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getStoredUser, getStoredToken, clearAuth, getStoredRefresh } from '../api';
import {
  API, API_V1, DEPARTMENTS, AGENT_ESCALATION, AGENT_FUNCTION_DEPTS, DEPT_REVERSE_MAP,
  ROLES, Role, calculateHealthScore, authFetch,
} from '../constants';
import { realtimeService } from '../services/realtimeService';

// RBAC permission map (mirrors backend auth/permissions.py)
const PERMISSIONS: Record<string, string[]> = {
  approve_action:      ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER'],
  reject_action:       ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER'],
  transfer_action:     ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER'],
  escalate_action:     ['ADMIN', 'CFO', 'CONTROLLER'],
  bulk_approve:        ['ADMIN', 'CFO', 'CONTROLLER'],
  confirm_provisional: ['ADMIN', 'CFO', 'CONTROLLER'],
  reverse_provisional: ['ADMIN', 'CFO'],
  upload_exceptions:   ['ADMIN', 'CFO', 'CONTROLLER'],
  view_exceptions:     ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER', 'AUDITOR', 'VIEWER'],
  resolve_exceptions:  ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER'],
  manage_guardrails:   ['ADMIN', 'CFO'],
  reset_demo:          ['ADMIN'],
  view_financials:     ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER', 'AUDITOR', 'VIEWER'],
  export_jes:          ['ADMIN', 'CFO', 'CONTROLLER'],
  manage_periods:      ['ADMIN', 'CFO', 'CONTROLLER'],
  generate_ia_report:  ['ADMIN', 'CFO', 'AUDITOR'],
  view_ia_report:      ['ADMIN', 'CFO', 'CONTROLLER', 'AUDITOR', 'VIEWER'],
  export_ia_report:    ['ADMIN', 'CFO', 'AUDITOR'],
  view_controls:       ['ADMIN', 'CFO', 'CONTROLLER', 'AUDITOR', 'VIEWER'],
  view_ai_governance:  ['ADMIN', 'CFO', 'CONTROLLER', 'AUDITOR', 'VIEWER'],
  manage_users:        ['ADMIN'],
  upload_gl:           ['ADMIN', 'CFO', 'CONTROLLER'],
  view_dashboard:      ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER', 'AUDITOR', 'VIEWER'],
};

// Types
interface AuthUser {
  id: number;
  email: string;
  full_name: string;
  role: string;       // ADMIN | CFO | CONTROLLER | REVIEWER | AUDITOR | VIEWER
  is_admin: boolean;
  organization_id: number | null;
  department: string | null;
  doa_level: string | null;  // L2..L7 — drives canApprove() checks
}

interface AppState {
  token: string | null;
  authUser: AuthUser | null;
  role: Role;
  entity: string;
  entities: any[];
  period: string;
  derivedPeriods: string[];
  orgData: any;
  dashData: any;
  personas: any[];
  skills: any[];
  allExceptions: any[];
  filteredExceptions: any[];
  loading: boolean;
  apiError: string | null;
  setRole: (r: Role) => void;
  setEntity: (e: string) => void;
  setPeriod: (p: string) => void;
  refreshData: () => void;
  getDeptStats: (deptName: string) => DeptStats;
  getExceptionsForDept: (deptName: string) => any[];
  getExceptionsForAgent: (agentId: string) => { actionable: any[]; readOnly: any[] };
  getAgentsForDept: (deptName: string) => any[];
  getPendingForRole: () => any[];
  visibleDepartments: string[];
  hasPermission: (permission: string) => boolean;
  logout: () => void;
  onLoginSuccess: () => void;
}

export interface DeptStats {
  agents: number; skills: number; exceptions: number; atRisk: number;
  flagged: number; escalated: number; processing: number; resolved: number;
  autoResolved: number; health: number;
}

const AppContext = createContext<AppState>(null!);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [authUser, setAuthUser] = useState<AuthUser | null>(getStoredUser());
  const [role, setRole] = useState<Role>(ROLES[0]); // Default: CFO
  // Global legal-entity scope. Persisted to localStorage so it survives page
  // refreshes and switching browser tabs. '' = All Entities (consolidated).
  const [entity, setEntityState] = useState<string>(() => {
    try { return localStorage.getItem('agentcfo_entity') || ''; } catch { return ''; }
  });
  const setEntity = useCallback((e: string) => {
    setEntityState(e);
    try {
      if (e) localStorage.setItem('agentcfo_entity', e);
      else localStorage.removeItem('agentcfo_entity');
    } catch { /* ignore */ }
  }, []);
  const [entities, setEntities] = useState<any[]>([]);
  const [period, setPeriod] = useState('');  // '' = All Time
  const [orgData, setOrgData] = useState<any>(null);
  const [dashData, setDashData] = useState<any>(null);
  const [personas, setPersonas] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [allExceptions, setAllExceptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    // If no token, skip data loading — user must log in
    const currentToken = getStoredToken();
    if (!currentToken) {
      setToken(null);
      setAuthUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setApiError(null);

    // Step 1: Verify token with /auth/me
    try {
      const meRes = await authFetch(`${API_V1}/auth/me`);
      if (!meRes.ok) {
        // Token invalid — clear auth state
        clearAuth();
        setToken(null);
        setAuthUser(null);
        setLoading(false);
        return;
      }
      const meData = await meRes.json();
      if (meData && meData.id) {
        const u: AuthUser = {
          id: meData.id,
          email: meData.email,
          full_name: meData.full_name,
          role: (meData.role || 'VIEWER').toUpperCase(),
          is_admin: meData.is_admin,
          organization_id: meData.organization_id,
          department: meData.department,
          doa_level: meData.doa_level || null,
        };
        setAuthUser(u);
        localStorage.setItem('agentcfo_user', JSON.stringify(u));
      }
    } catch {
      clearAuth();
      setToken(null);
      setAuthUser(null);
      setLoading(false);
      return;
    }

    // Step 2: Load app data
    try {
      const ep = entity ? `&entity_code=${entity}` : '';
      // Include role context for per-exception permissions
      const roleDept = Array.isArray(role.departments) ? role.departments[0] || 'all' : 'all';
      const permParams = `&agent_level=${role.level}&agent_department=${encodeURIComponent(roleDept)}`;
      const oid = authUser?.organization_id || 1;
      const results = await Promise.all([
        authFetch(`${API}/org-summary?org_id=${oid}${ep}${permParams}`),
        authFetch(`${API}/dashboard?org_id=${oid}${ep}${permParams}`),
        authFetch(`${API}?org_id=${oid}&limit=5000&offset=0${ep}${permParams}`),
        authFetch(`${API}/entities?org_id=${oid}${permParams}`),
      ]);

      // Check for server errors (5xx) — ignore 404s (empty data) and 401s (auth)
      const serverErrors = results.filter(r => !r.ok && r.status >= 500);
      if (serverErrors.length > 0) {
        const codes = serverErrors.map(r => r.status).join(', ');
        console.error(`API server error: ${codes}`);
        setApiError(`Backend server error (${codes}). Some data may be incomplete.`);
      }

      const [orgRes, dashRes, excRes, entRes] = results;
      if (orgRes.ok) setOrgData(await orgRes.json());
      if (dashRes.ok) setDashData(await dashRes.json());
      if (excRes.ok) {
        const excData = await excRes.json();
        setAllExceptions(excData.exceptions || []);
      }
      if (entRes.ok) {
        const entData = await entRes.json();
        setEntities(Array.isArray(entData) ? entData : []);
      }

      // Load agent data
      try {
        const t = getStoredToken();
        if (t) {
          const headers = { Authorization: `Bearer ${t}` };
          const [pRes, sRes] = await Promise.all([
            axios.get(`${API_V1}/agents/personas`, { headers }),
            axios.get(`${API_V1}/agents/skills`, { headers }),
          ]);
          setPersonas(pRes.data.personas || []);
          setSkills(sRes.data.skills || []);
        }
      } catch (err) {
        console.warn('Agent data unavailable:', err);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setApiError('Unable to connect to Agent Workforce backend. Check that the server is running on ' + API.split('/api')[0]);
    }
    setLoading(false);
  }, [entity, role]);

  useEffect(() => { loadData(); }, [loadData]);

  // SSE: connect real-time event stream when authenticated
  useEffect(() => {
    const currentToken = getStoredToken();
    if (!currentToken) return;

    realtimeService.connect(currentToken);

    const unsubs = [
      realtimeService.on('exception_update', () => { loadData(); }),
      realtimeService.on('approval_update', () => { loadData(); }),
      realtimeService.on('resolution_update', () => { loadData(); }),
      realtimeService.on('notification', () => {
        // Notification count is updated by TopBar via SSE (no polling)
      }),
      realtimeService.on('connection_lost', () => {
        console.warn('SSE connection lost -- polling fallback may be needed');
      }),
    ];

    return () => {
      unsubs.forEach(unsub => unsub());
      realtimeService.disconnect();
    };
  }, [token, loadData]);

  // Logout: clear all auth state, best-effort server-side token revocation
  const logout = useCallback(() => {
    const refreshToken = getStoredRefresh();
    if (refreshToken) {
      fetch(`${API_V1}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {});
    }
    clearAuth();
    setToken(null);
    setAuthUser(null);
    setOrgData(null);
    setDashData(null);
    setAllExceptions([]);
    setEntities([]);
    setPersonas([]);
    setSkills([]);
  }, []);

  // Called by Login page after successful login to trigger data loading
  const onLoginSuccess = useCallback(() => {
    const t = getStoredToken();
    const u = getStoredUser();
    setToken(t);
    if (u) setAuthUser(u);
    loadData();
  }, [loadData]);

  // Filter exceptions by selected entity (server already filters, but keep client-side as fallback)
  const filteredExceptions = useMemo(() => {
    if (!entity) return allExceptions;
    return allExceptions.filter((e: any) => e.jurisdiction === entity);
  }, [allExceptions, entity]);

  const derivedPeriods = useMemo(() => {
    const quarters = new Set<string>();
    for (const exc of allExceptions) {
      const d = exc.created_at ? new Date(exc.created_at) : null;
      if (d && !isNaN(d.getTime())) {
        const q = `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
        quarters.add(q);
      }
    }
    return Array.from(quarters).sort().reverse();
  }, [allExceptions]);

  const getDeptStats = useCallback((deptName: string): DeptStats => {
    const deptConfig = DEPARTMENTS[deptName];
    if (!deptConfig) return { agents: 0, skills: 0, exceptions: 0, atRisk: 0, flagged: 0, escalated: 0, processing: 0, resolved: 0, autoResolved: 0, health: 100 };
    const deptAgents = personas.filter(p => deptConfig.agents.includes(p.id));
    const agentCount = deptAgents.length;
    const skillCount = deptAgents.reduce((sum: number, a: any) => sum + (a.skills_count || 0), 0);

    // Compute stats from filteredExceptions (respects entity filter)
    const dbDepts = DEPT_REVERSE_MAP[deptName] || [];
    const deptExcs = filteredExceptions.filter((e: any) =>
      dbDepts.includes(e.department) || dbDepts.includes((e.department || '').toLowerCase().replace(/\s+/g, '_'))
    );
    const exceptions = deptExcs.length;
    const atRisk = deptExcs.reduce((s: number, e: any) => s + (e.amount || 0), 0);
    const flagged = deptExcs.filter((e: any) => (e.status || '').toLowerCase() === 'flagged').length;
    const escalated = deptExcs.filter((e: any) => (e.status || '').toLowerCase() === 'escalated').length;
    const processing = deptExcs.filter((e: any) => (e.status || '').toLowerCase() === 'processing').length;
    const resolved = deptExcs.filter((e: any) => (e.status || '').toLowerCase().startsWith('resolved')).length;

    const health = calculateHealthScore({ exceptions, resolved, escalated, flagged });
    return { agents: agentCount, skills: skillCount, exceptions, atRisk, flagged, escalated, processing, resolved, autoResolved: 0, health };
  }, [filteredExceptions, personas]);

  const getExceptionsForDept = useCallback((deptName: string): any[] => {
    const dbDepts = DEPT_REVERSE_MAP[deptName] || [];
    return filteredExceptions.filter((e: any) =>
      dbDepts.includes(e.department) || dbDepts.includes((e.department || '').toLowerCase().replace(/\s+/g, '_'))
    );
  }, [filteredExceptions]);

  const getExceptionsForAgent = useCallback((agentId: string): { actionable: any[]; readOnly: any[] } => {
    // Find which department this agent belongs to
    const deptName = Object.entries(DEPARTMENTS).find(([, cfg]) => cfg.agents.includes(agentId))?.[0];
    if (!deptName) return { actionable: [], readOnly: [] };
    const agentEsc = AGENT_ESCALATION[agentId] || { level: 'L2', limit: 50000 };
    const agentDepts = AGENT_FUNCTION_DEPTS[agentId]; // AP/AR functional filter

    // Get department exceptions + cross-department escalations to this agent's level
    const deptExcs = getExceptionsForDept(deptName);
    const deptIds = new Set(deptExcs.map((e: any) => e.id));
    const crossDeptEscalated = filteredExceptions.filter(e =>
      e.escalation_level === agentEsc.level && !deptIds.has(e.id)
    );
    const crossDeptIds = new Set(crossDeptEscalated.map((e: any) => e.id));
    const allRelevant = [...deptExcs, ...crossDeptEscalated];

    // Filter out resolved/dismissed exceptions from active queues
    const RESOLVED_STATUSES = ['resolved_auto', 'resolved_human', 'dismissed'];
    const activeExcs = allRelevant.filter(e => {
      if (RESOLVED_STATUSES.includes((e.status || '').toLowerCase())) return false;
      // Only apply functional dept filter to same-dept exceptions (not cross-dept escalations)
      if (agentDepts && !crossDeptIds.has(e.id) && !agentDepts.includes(e.department)) return false;
      return true;
    });

    // Use backend permissions when available; fallback to amount comparison
    const actionable = activeExcs.filter(e => {
      if (e.permissions) return e.permissions.can_approve;
      return (e.amount || 0) <= agentEsc.limit;
    });
    const actionableIds = new Set(actionable.map((e: any) => e.id));
    const readOnly = activeExcs.filter(e => !actionableIds.has(e.id));
    return { actionable, readOnly };
  }, [getExceptionsForDept, filteredExceptions]);

  const getAgentsForDept = useCallback((deptName: string): any[] => {
    const deptConfig = DEPARTMENTS[deptName];
    return deptConfig ? personas.filter(p => deptConfig.agents.includes(p.id)) : [];
  }, [personas]);

  const getPendingForRole = useCallback((): any[] => {
    const RESOLVED_STATUSES = ['resolved_auto', 'resolved_human', 'dismissed'];
    const active = filteredExceptions.filter(e => !RESOLVED_STATUSES.includes((e.status || '').toLowerCase()));

    // Use backend permissions when available; fallback to DOA limit from AGENT_ESCALATION
    return active
      .filter(e => {
        if (e.permissions) return e.permissions.can_view;
        // Fallback: CFO sees all, others see within DOA ceiling
        if (role.level === 'L6') return true;
        const agentEsc = AGENT_ESCALATION[role.id] || { level: role.level, limit: 50000000 };
        return (e.amount || 0) <= agentEsc.limit;
      })
      .sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0));
  }, [filteredExceptions, role]);

  const visibleDepartments = useMemo(() => {
    if (role.departments === 'all') return Object.keys(DEPARTMENTS);
    return role.departments;
  }, [role]);

  const hasPermission = useCallback((permission: string): boolean => {
    const userRole = (authUser?.role || 'VIEWER').toUpperCase();
    const allowed = PERMISSIONS[permission] || [];
    return allowed.includes(userRole);
  }, [authUser]);

  const value: AppState = {
    token, authUser, role, entity, entities, period, derivedPeriods, orgData, dashData, personas, skills,
    allExceptions, filteredExceptions, loading, apiError,
    setRole, setEntity, setPeriod, refreshData: loadData,
    getDeptStats, getExceptionsForDept, getExceptionsForAgent, getAgentsForDept,
    getPendingForRole, visibleDepartments, hasPermission,
    logout, onLoginSuccess,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
